import React, { useState, useCallback, useMemo } from "react";
import I18n from "i18n-js";
import axios from "axios";

import Button from "./Button";
import { DropdownSelect } from "./DropdownSelect";

const redirectTo = (url: string) => {
  console.log("redirect to", url);

  if (url !== "") {
    window.location.href = url;
    return true;
  }

  return false;
};

const submitToNeo = (
  submitUrl: string,
  authenticityToken: string,
  teamName: string,
  favoriteTeamId: number | undefined,
  marketingOptIn: boolean
) => {
  return new Promise<void>((resolve, reject) => {
    const data = {
      authenticity_token: authenticityToken,
      team_name: teamName,
      favorite_team_id: favoriteTeamId,
      marketing_opt_in: marketingOptIn,
    };

    axios
      .post(submitUrl, data)
      .then(({ data }) => {
        if (data?.error) {
          reject(data.error);
        } else {
          resolve();
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

interface TeamOption {
  id: number;
  name: string;
}

interface NicknameFormProps {
  variant: "complete_profile" | "edit_profile";
  teamName: string;
  sso_profile_url: string;
  submit_url: string;
  redirect_after_submit_url: string;
  authenticity_token: string;
  receive_bet_reminders: boolean;
  reminder_email: string;
  marketing_opt_in: boolean;
  team_options?: TeamOption[];
}

const NicknameForm = ({
  variant, // "complete_profile" | "edit_profile"
  teamName: defaultTeamName,
  sso_profile_url,
  submit_url: submitUrl,
  redirect_after_submit_url: redirectAfterSubmitUrl,
  authenticity_token: authenticityToken,
  receive_bet_reminders: receiveBetReminders,
  reminder_email: defaultReminderEmail,
  marketing_opt_in: marketingOptIn,
  team_options: teamOptions,
}: NicknameFormProps) => {
  const [teamName, setTeamName] = useState(defaultTeamName || "");
  const [reminderEmail, setReminderEmail] = useState(
    defaultReminderEmail || ""
  );
  const [optinChecked, setOptInChecked] = useState(false);

  const [optInRequiredError, setOptInRequiredError] = useState(false);
  const [teamNameErrorMsg, setTeamNameErrorMsg] = useState("");
  const [favoriteTeamIdErrorMsg, setFavoriteTeamIdErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [marketingOptInChecked, setMarketingOptInChecked] = useState(
    marketingOptIn || false
  );
  const [favoriteTeamId, setFavoriteTeamId] = useState<number>();

  const onSelect = useCallback((option: TeamOption) => {
    setFavoriteTeamId(option.id);
  }, []);

  const submit = useCallback(() => {
    let earlyErrored = false;
    setOptInRequiredError(false);

    if (!earlyErrored) {
      setSubmitting(true);
      submitToNeo(
        submitUrl,
        authenticityToken,
        teamName,
        favoriteTeamId,
        marketingOptInChecked
      )
        .then(() => {
          if (!redirectTo(redirectAfterSubmitUrl)) {
            // could not redirect?
            setSubmitting(false);
          }
        })
        .catch((error) => {
          switch (error.type) {
            case "profile":
              console.error(error.message);
              break;
            case "team_name":
              setTeamNameErrorMsg(error.message);
              break;
            case "favorite_team_id":
              setFavoriteTeamIdErrorMsg(error.message);
              break;
          }
          setSubmitting(false);
        });
    } else {
      console.log("early error -> don't submit anything");
    }
  }, [
    teamName,
    favoriteTeamId,
    reminderEmail,
    marketingOptInChecked,
    optinChecked,
  ]);

  const onChangeTeamName = useCallback((e) => {
    setTeamName(e.target.value);
  }, []);
  const onMarketingOptInChange = useCallback((e) => {
    setMarketingOptInChecked(e.target.checked);
  }, []);
  const onOptInChecked = useCallback((e) => {
    setOptInChecked(e.target.checked);
  }, []);

  const options = useMemo(() => {
    if (!teamOptions) {
      return [];
    }

    return [
      ...teamOptions.map((option) => ({
        ...option,
        label: option.name,
      })),
      { name: "", label: I18n.t("complete_profile.no_favorite_team"), id: -1 },
    ];
  }, [teamOptions]);

  return (
    <div className="neo__nicknameForm__inner">
      <h3>
        <label htmlFor={"team_name"}>
          {I18n.t("complete_profile.team_name_label")}
        </label>
      </h3>
      <div>
        <input
          type="text"
          name="team_name"
          id="team_name"
          value={teamName}
          onChange={onChangeTeamName}
          autoFocus
          maxLength={32}
          className={
            "input input--higher" + (teamNameErrorMsg ? " input--error" : "")
          }
          placeholder={I18n.t("complete_profile.team_name_placeholder")}
        />
        {teamNameErrorMsg && (
          <span className="form__error form__error--centered">
            {teamNameErrorMsg}
          </span>
        )}
      </div>
      {teamOptions && (
        <>
          <h3>
            <label htmlFor="favorite_team">
              {I18n.t("complete_profile.favorite_team_label")}
            </label>
          </h3>
          <div>
            <DropdownSelect
              options={options}
              placeholder={I18n.t("complete_profile.favorite_team_placeholder")}
              onSelect={onSelect}
            />
            {favoriteTeamIdErrorMsg && (
              <span className="form__error form__error--centered">
                {favoriteTeamIdErrorMsg}
              </span>
            )}
          </div>
          <p className="completeProfile__additionalInfo">
            {I18n.t("complete_profile.favorite_team_info")}
          </p>
        </>
      )}
        { variant === "complete_profile" &&<>
            <div className="completeProfile__submitWrapper">
                <Button primary disabled={submitting} onClick={submit}>
                    {I18n.t("complete_profile.submit")}
                </Button>
            </div>
        </>
        }
        { variant === "edit_profile" && <>
            <p
                dangerouslySetInnerHTML={{
                    __html: I18n.t('complete_profile.profile_link', { profile_url: sso_profile_url })
                }}
            />
            <div className="completeProfile__submitWrapper">
                <Button primary disabled={submitting} onClick={submit}>
                    {I18n.t("complete_profile.save")}
                </Button>
            </div>
        </>
        }
    </div>
  );
};

export default NicknameForm;
